import { formatBytes, PLAN_CATALOG, type CharacterKey, type PaidPlan } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeError } from '../../lib/api/http.js';
import { characterImage } from '../../lib/characters.js';
import {
  canBuyHere,
  ENTITLEMENTS_KEY,
  otherChannel,
  otherChannelLine,
  priceLine,
  salesHere,
  startCheckout,
  STORE_SILENT,
  trialHere,
  useEntitlements,
  useStorePrices,
  type PaywallReason,
} from '../../lib/plan.js';
import { isNativeShell } from '../../lib/platform.js';
import { storeReady } from '../../lib/storeBilling.js';
import { track } from '../../lib/track.js';
import { DocLink } from '../legal/Consent.js';
import { CreditsPanel } from './CreditsPanel.js';
import { areaObject } from '../../lib/areas.js';
import { DELIVERY_SAMPLES } from '../../lib/deliverySamples.js';
import './paywall.css';

type Pitch = { kicker: string; title: string; lead: string };

/** O que dizer em cada momento: a pessoa esbarrou em algo e a folha fala disso. */
const PITCH: Record<PaywallReason, Pitch> = {
  limit: {
    kicker: 'Pedidos de hoje',
    title: `Seus ${PLAN_CATALOG.free.aiDailyLimit ?? 5} pedidos de hoje acabaram`,
    lead: 'Amanhã cedo eles voltam. Pra continuar agora, escolha um plano ou use créditos extras.',
  },
  missed: {
    kicker: 'Enquanto você não estava',
    title: 'Teve gente que ficou sem resposta',
    lead: 'Fiquei sem pedidos pra responder por você hoje. Com um plano, eu não paro no meio do dia.',
  },
  meeting: {
    kicker: 'Reunião de especialistas',
    title: 'Decisão difícil? A equipe inteira opina',
    lead: 'Cada área olha um lado e eu fecho a decisão com você, já com os próximos passos.',
  },
  services: {
    kicker: 'Serviços prontos',
    title: 'Peça e receba pronto',
    lead: 'Raio-X do mês, roteiro de viagem, carta de reclamação: a equipe prepara e eu te entrego.',
  },
  research: {
    kicker: 'Pesquisa',
    title: 'Eu pesquiso, comparo e te trago a resposta',
    lead: 'Com a pesquisa na internet ligada em Ajustes, eu busco, comparo e trago a resposta com as fontes.',
  },
  news: {
    kicker: 'Notícias',
    title: 'Sua edição completa, escrita pra você',
    lead: 'O que acontece na sua cidade e no seu trabalho, explicado com o porquê de importar pra você. Com alertas dos seus assuntos e o resumo da semana.',
  },
  clients: {
    kicker: 'Meu negócio',
    title: 'Cliente não espera. Eu atendo por você',
    lead: 'Atendo seus clientes com o jeito do seu negócio, marco horário e te chamo só quando precisa.',
  },
  'quick-replies': {
    kicker: 'Respostas prontas',
    title: 'Chega de copiar e colar',
    lead: 'Suas respostas ficam a um toque de distância, e eu também uso com os seus clientes.',
  },
  schedule: {
    kicker: 'Avisos programados',
    title: 'Um aviso só, pra todos os seus clientes',
    lead: 'Você escreve uma vez, escolhe quem recebe e eu envio na hora marcada.',
  },
  agenda: {
    kicker: 'Agenda de atendimento',
    title: 'Seus clientes marcam sozinhos',
    lead: 'Eu ofereço os horários livres, confirmo e lembro na véspera.',
  },
  avatar: {
    kicker: 'Crie o seu',
    title: 'Mais um avatar com a sua cara',
    lead: `O primeiro foi por nossa conta. No ${PLAN_CATALOG.basic.name} e no ${PLAN_CATALOG.pro.name} você faz outros, em outros estilos, quando quiser.`,
  },
  voice: {
    kicker: 'Conversa ao vivo',
    title: 'Seus minutos de conversa ao vivo acabaram',
    lead: `No dia 1 eles voltam. Com o ${PLAN_CATALOG.basic.name} são ${PLAN_CATALOG.basic.voiceMinutesMonthly} minutos por mês, e com o ${PLAN_CATALOG.pro.name}, ${PLAN_CATALOG.pro.voiceMinutesMonthly}.`,
  },
};

/** No Essencial, a conversa ao vivo é uma amostra única: acabou, não volta no mês. */
const VOICE_SAMPLE_PITCH: Pitch = {
  kicker: 'Conversa ao vivo',
  title: `Sua amostra de ${PLAN_CATALOG.free.voiceSampleMinutes} minutos acabou`,
  lead: `Gostou de conversar ao vivo? Com o ${PLAN_CATALOG.basic.name} são ${PLAN_CATALOG.basic.voiceMinutesMonthly} minutos por mês, e com o ${PLAN_CATALOG.pro.name}, ${PLAN_CATALOG.pro.voiceMinutesMonthly}.`,
};

/** O Essencial também tem um teto no mês: quando ele acaba, a conversa é sobre o mês. */
const FREE_MONTH_PITCH: Pitch = {
  kicker: 'Pedidos do mês',
  title: `Seus ${(PLAN_CATALOG.free.aiMonthlyLimit ?? 0).toLocaleString('pt-BR')} pedidos do mês acabaram`,
  lead: 'No dia 1 eles voltam. Pra continuar agora, escolha um plano ou use créditos extras.',
};

/**
 * Amostra do que a pessoa recebe, no lugar de só prometer: a capa de uma
 * entrega de exemplo ou a decisão de uma reunião de exemplo.
 */
function PitchPreview({ reason }: { reason: PaywallReason }) {
  if (reason === 'services') {
    const sample = DELIVERY_SAMPLES.raio_x;
    const cover = sample.document.cover;
    return (
      <div className="pw-preview">
        <img src={areaObject('dinheiro')} alt="" />
        <span>
          <small>Exemplo de entrega · {sample.title}</small>
          {cover && <b>{cover.value}</b>}
          {cover && <em>{cover.label}</em>}
        </span>
      </div>
    );
  }
  if (reason === 'meeting') {
    return (
      <div className="pw-preview dark">
        <img src={areaObject('informacao')} alt="" />
        <span>
          <small>Exemplo de decisão · Vale trocar de celular agora?</small>
          <b className="text">Esperar o dia 12 e comprar quando o preço voltar ao menor valor.</b>
        </span>
      </div>
    );
  }
  return null;
}

const count = (value: number | null) => (value ?? 0).toLocaleString('pt-BR');

/** O que cada plano dá, nos números do catálogo (o servidor pode ajustá-los). */
const BENEFITS: Record<PaidPlan, string[]> = {
  basic: [
    `${count(PLAN_CATALOG.basic.aiMonthlyLimit)} pedidos ao Secretário por mês`,
    `${PLAN_CATALOG.basic.voiceMinutesMonthly} minutos de conversa ao vivo por mês`,
    `${PLAN_CATALOG.basic.deliveriesMonthly} documentos prontos por mês`,
    `${formatBytes(PLAN_CATALOG.basic.savedFilesMb * 1024 * 1024)} para fotos e PDFs, com busca pelo que está escrito dentro`,
    'Respostas automáticas que não param no meio do dia',
  ],
  pro: [
    `Tudo do ${PLAN_CATALOG.basic.name}`,
    `${count(PLAN_CATALOG.pro.aiMonthlyLimit)} pedidos ao Secretário por mês`,
    `${PLAN_CATALOG.pro.voiceMinutesMonthly} minutos de conversa ao vivo por mês`,
    `${PLAN_CATALOG.pro.deliveriesMonthly} documentos prontos por mês`,
    `${formatBytes(PLAN_CATALOG.pro.savedFilesMb * 1024 * 1024)} para fotos e PDFs`,
    'Raciocínio com calma nos pedidos difíceis',
    'Notícias completas',
  ],
};

/**
 * Folha de planos. Abre no momento em que a pessoa esbarra num limite ou num
 * recurso de plano maior, mostra o ganho e leva ao pagamento, guardando onde
 * ela estava para voltar depois.
 */
export function PaywallSheet({
  reason,
  surface,
  characterKey,
  onClose,
}: {
  reason: PaywallReason;
  surface: string;
  characterKey: CharacterKey;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const entitlementsQ = useEntitlements();
  const store = useStorePrices();
  const current = entitlementsQ.data?.plan ?? 'free';
  // Só o que este canal (site ou loja do aparelho) vende agora e que está acima do plano atual.
  const sales = salesHere(entitlementsQ.data);
  const offers: PaidPlan[] = (['basic', 'pro'] as const).filter(
    (plan) => sales[plan] && (plan === 'pro' || current === 'free'),
  );
  const proOnly = reason !== 'limit' && reason !== 'missed' && reason !== 'meeting';
  const visible = proOnly ? offers.filter((plan) => plan === 'pro') : offers;
  const [picked, setPicked] = useState<PaidPlan | null>(null);
  const choice = picked && visible.includes(picked) ? picked : (visible[0] ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const monthOut = entitlementsQ.data?.features.ai.remainingMonth === 0;
  // Plano pago no teto do mês: a conversa é sobre o mês, não sobre o dia.
  const pitch =
    reason === 'limit' && current !== 'free'
      ? {
          kicker: 'Pedidos do mês',
          title: 'Os pedidos do mês do seu plano acabaram',
          lead: 'Com créditos extras eu continuo agora. Na virada do mês eles voltam.',
        }
      : reason === 'limit' && monthOut
        ? FREE_MONTH_PITCH
        : reason === 'voice' && entitlementsQ.data?.features.voice.sample
          ? VOICE_SAMPLE_PITCH
          : PITCH[reason];
  const offersCredits = reason === 'limit' || reason === 'missed' || reason === 'voice';
  // A assinatura veio de outro canal (site num app, loja no site): a troca é por lá.
  const elsewhere = otherChannel(entitlementsQ.data?.subscription ?? null);
  // No app, a loja é quem diz o preço: sem ele, a compra espera a loja voltar.
  const native = isNativeShell();
  const storeSilent = storeReady() && !store.loading && (store.failed || !store.prices);
  const buyable =
    canBuyHere() && !elsewhere && (!native || !!priceLine(choice ?? 'basic', store.prices));
  // Teste grátis do plano escolhido, quando vale para esta pessoa e por este canal.
  const trialDays = choice ? trialHere(choice, entitlementsQ.data, store.prices) : 0;

  useEffect(() => {
    track('paywall_viewed', { surface, reason });
  }, [surface, reason]);

  async function buy() {
    if (!choice || busy) return;
    setBusy(true);
    setError('');
    track('checkout_started', { plan: choice, surface, reason });
    try {
      // Na volta do pagamento, a pessoa cai de novo onde estava.
      try {
        window.sessionStorage.setItem(
          'secretario.plano.volta',
          window.location.pathname + window.location.search,
        );
      } catch {
        /* sem armazenamento: volta para o plano */
      }
      const changed = await startCheckout(choice, entitlementsQ.data);
      if (changed) {
        await queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
        onClose();
      }
      // Na loja, a pessoa pode ter fechado a tela de compra.
      setBusy(false);
    } catch (failure) {
      setError(describeError(failure));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="pw-back" onClick={onClose}>
      <div
        className={`pw${choice === 'pro' ? ' pro' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-title"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="pw-halo" aria-hidden="true" />
        <img
          className="pw-art"
          src={characterImage(characterKey, choice === 'pro' ? 'elegante' : 'tranquilo')}
          alt=""
        />
        <div className="pw-body">
          <div className="pw-kicker">{pitch.kicker}</div>
          <h2 id="pw-title">{pitch.title}</h2>
          <p className="pw-lead">{pitch.lead}</p>
          <PitchPreview reason={reason} />
          {offersCredits && <CreditsPanel compact surface={`paywall-${reason}`} />}

          {visible.length > 0 ? (
            <div className="pw-plans" role="radiogroup" aria-label="Planos">
              {visible.map((plan) => (
                <button
                  key={plan}
                  type="button"
                  role="radio"
                  aria-checked={choice === plan}
                  className={`pw-plan ${plan}${choice === plan ? ' on' : ''}`}
                  onClick={() => setPicked(plan)}
                >
                  <span className="pw-plan-hd">
                    <b>{PLAN_CATALOG[plan].name}</b>
                    {priceLine(plan, store.prices) && <i>{priceLine(plan, store.prices)}</i>}
                  </span>
                  <ul>
                    {BENEFITS[plan].map((benefit) => (
                      <li key={benefit}>{benefit}</li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          ) : (
            <p className="pw-note">
              {reason === 'limit' || reason === 'missed'
                ? 'Seus pedidos voltam amanhã cedo.'
                : 'Esse recurso ainda não está disponível pra assinar.'}
            </p>
          )}

          {error && <p className="pw-error">{error}</p>}
          <div className="pw-acts">
            {choice && buyable && (
              <button className="pw-cta" onClick={() => void buy()} disabled={busy}>
                {busy
                  ? 'Abrindo o pagamento…'
                  : trialDays > 0
                    ? `Começar ${trialDays} dias grátis`
                    : `Assinar o ${PLAN_CATALOG[choice].name}`}
              </button>
            )}
            {choice && !buyable && (
              <p className="pw-note">
                {elsewhere
                  ? otherChannelLine(elsewhere)
                  : storeSilent
                    ? STORE_SILENT
                    : 'A assinatura ainda não está disponível aqui no app.'}
              </p>
            )}
            <button className="pw-later" onClick={onClose} disabled={busy}>
              Agora não
            </button>
          </div>
          {choice && buyable && (
            <p className="pw-fine">
              {trialDays > 0
                ? `${trialDays} dias grátis, depois ${priceLine(choice, store.prices) ?? ''}. Cancele antes do fim do teste e nada é cobrado. `
                : 'Mensal, renova sozinho. Cancele quando quiser. '}
              <DocLink doc="termos">Termos</DocLink> e{' '}
              <DocLink doc="privacidade">Privacidade</DocLink>.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
